import JSZip from "jszip";
import { toast } from "react-toastify";
import { swingLibraryService } from "../../../services/swingLibrary";
import { formatUnit } from "./formatUnit";

export function downloadFile(blob, filename) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

// ---------- Small utils ----------
const csvEscape = (val) => {
  const str = val == null ? "" : String(val);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const formatDate = (ms) => {
  if (!ms) return "";
  const d = new Date(Number(ms));
  //  MM/DD/YY HH:mm, 24h
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yy} ${hh}:${min}`;
};

// ---------- Public: export CSV ----------
export async function downloadJSONAsCSV(params) {
  const { filename, indicators, indicatorsUnits, userId, videoIds } = params;
  const tid = toast.loading("Preparing CSV…");
  try {
    const enriched = await swingLibraryService.exportSwingList({
      userId,
      videoIds,
    });
    const enrichedVideos = enriched?.enrichedVideos ?? [];

    const aggregates = {};
    for (const ind of indicators) {
      const key = ind.id;
      const values = enrichedVideos
        .map((v) => v?.analysis?.[key]?.indicatorValue)
        .filter((n) => typeof n === "number" && !Number.isNaN(n));
      if (values.length === 0) {
        aggregates[key] = { avg: 0, std: 0 };
        continue;
      }
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const variance =
        values.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) /
        values.length;
      const std = Math.sqrt(variance);
      aggregates[key] = { avg, std };
    }

    const statsRows = [
      {
        analysis: Object.fromEntries(
          Object.entries(aggregates).map(([key, v]) => [
            key,
            { indicatorValue: v.avg },
          ]),
        ),
        tags: [],
        userData: { fullName: "", golfClub: "" },
        videoPath: "Averages",
      },
      {
        analysis: Object.fromEntries(
          Object.entries(aggregates).map(([key, v]) => [
            key,
            { indicatorValue: v.std },
          ]),
        ),
        tags: [],
        userData: { fullName: "", golfClub: "" },
        videoPath: "Std Dev",
      },
    ];

    const allRows = [...statsRows, ...enrichedVideos];
    const { csvData } = flattenData(allRows, indicators, indicatorsUnits);

    const blob = new Blob([csvData], { type: "text/csv" });
    downloadFile(blob, `${filename}.csv`);
    toast.success("CSV saved");
  } catch (err) {
    console.error("downloadCSV", err);
    toast.error(err?.message ?? "Failed to prepare CSV");
  } finally {
    toast.dismiss(tid);
  }
}

// ---------- Public: export arbitrary JSON ----------
export async function downloadJSONReport(data) {
  const tid = toast.loading("Preparing JSON…");
  try {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    downloadFile(blob, `${Date.now()}-report.json`);
    toast.success("JSON saved");
  } catch (err) {
    console.error("downloadJSONReport", err);
    toast.error(err?.message ?? "Failed to prepare JSON");
  } finally {
    toast.dismiss(tid);
  }
}

// ---------- Download multiple video files as .zip (or single as file) ----------
export async function downloadVideosHelper(videos) {
  const entries = Object.entries(videos);
  if (entries.length === 0) return;
  const tid = toast.loading("Downloading videos…");
  try {
    const names = entries.map(([, v]) => {
      const ext =
        v?.videoType && v.videoType !== "video/*" ? `.${v.videoType}` : "";
      return `${v?.videoOrigName ?? "video"}${ext}`;
    });

    if (entries.length === 1) {
      const [, single] = entries[0];
      const blob = await fetchBlob(single.videoPath);
      downloadFile(blob, names[0]);
      toast.success("Video saved");
      return;
    }

    const zip = new JSZip();
    const blobs = await Promise.allSettled(
      entries.map(([, v]) => fetchBlob(v.videoPath)),
    );
    blobs.forEach((res, i) => {
      if (res.status === "fulfilled") zip.file(names[i], res.value);
      else console.warn("Failed to download", names[i]);
    });
    const archive = await zip.generateAsync({ type: "blob" });
    downloadFile(archive, "videos.zip");
    toast.success("Archive saved");
  } catch (err) {
    console.error("downloadVideosHelper", err);
    toast.error(err?.message ?? "Failed to download videos");
  } finally {
    toast.dismiss(tid);
  }
}

async function fetchBlob(url) {
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return await res.blob();
}

// ---------- CSV helpers ----------
function flattenData(jsonData, indicators, indicatorsUnits) {
  const columns = [
    "Video",
    "Date",
    "Golf Club",
    "Name",
    "Tags",
    ...indicators.map((el) => el.name),
  ];
  let csv = columns.join(",") + "\n";

  for (const row of jsonData) {
    const line = columns
      .map((col) => {
        switch (col) {
          case "Date":
            return csvEscape(formatDate(row?.videoCreated));
          case "Golf Club":
            return csvEscape(row?.userData?.golfClub ?? "");
          case "Name":
            return csvEscape(row?.userData?.fullName ?? "");
          case "Tags": {
            const tags = Array.isArray(row?.tags) ? row.tags.join(", ") : "";
            return csvEscape(tags);
          }
          case "Video":
            return csvEscape(row?.videoPath ?? "");
          default: {
            const ind = indicators.find((el) => el.name === col);
            const key = ind?.id;
            const unit = key ? indicatorsUnits[key]?.unit : undefined;
            const value = key
              ? row?.analysis?.[key]?.indicatorValue
              : undefined;
            if (value == null) return "";
            // Show no unit symbol in CSV, and allow unit-based default precision
            return csvEscape(
              formatUnit(unit, value, { convert: true, showSymbol: false }),
            );
          }
        }
      })
      .join(",");
    csv += line + "\n";
  }

  return { columns, csvData: csv };
}
